// SPDX-License-Identifier: LicenseRef-Proprietary
import React from "react";
import Plot from "react-plotly.js";
import { TimeSeriesData, PieData } from "../lib/models";

interface TimelineChartProps {
  data: TimeSeriesData[];
  title?: string;
}

export const TimelineChart: React.FC<TimelineChartProps> = ({
  data,
  title = "Incidence and savings over time",
}) => {
  const plotData = [
    {
      x: data.map((d) => d.year),
      y: data.map((d) => d.baselineCases),
      type: "scatter" as const,
      mode: "lines+markers" as const,
      name: "Baseline cases",
      line: { color: "#3b82f6" },
    },
    {
      x: data.map((d) => d.year),
      y: data.map((d) => d.withVaccineCases),
      type: "scatter" as const,
      mode: "lines+markers" as const,
      name: "With vaccine",
      line: { color: "#10b981" },
    },
  ];

  const layout = {
    title: { text: title, font: { size: 16 } },
    xaxis: { title: { text: "Time" } },
    yaxis: { title: { text: "Cases" } },
    showlegend: true,
    margin: { l: 50, r: 20, t: 50, b: 50 },
    autosize: true,
    hovermode: "x unified" as const,
  };

  const config = {
    displayModeBar: true,
    displaylogo: false,
    responsive: true,
    modeBarButtonsToRemove: ["pan2d", "lasso2d", "select2d"] as any,
  };

  return (
    <div className="vaccine-demo__chart-container">
      <div className="vaccine-demo__chart-header">
        <div className="vaccine-demo__chart-title">{title}</div>
      </div>
      <div className="vaccine-demo__chart">
        <Plot
          data={plotData}
          layout={layout}
          config={config}
          style={{ width: "100%", height: "400px" }}
          useResizeHandler
        />
      </div>
    </div>
  );
};

interface PieChartProps {
  data: PieData[];
  title?: string;
}

export const PieChart: React.FC<PieChartProps> = ({
  data,
  title = "Cost components",
}) => {
  // Standardized colors matching Cost Composition
  const colors = ["#4C8DFF", "#5EDFCB", "#6A5BE8", "#8BB0FF", "#3b82f6"];

  const plotData = [
    {
      labels: data.map((d) => d.name),
      values: data.map((d) => d.value),
      type: "pie" as const,
      marker: {
        colors: colors.slice(0, data.length),
      },
      textinfo: "label+percent" as any,
      textposition: "outside" as any,
      textfont: { color: '#f1f5f9' },
      hovertemplate: '%{label}<br>%{percent} (€%{value:,.0f})<extra></extra>',
    },
  ];

  const layout = {
    title: { text: title, font: { size: 16, color: '#f1f5f9' } },
    showlegend: true,
    legend: { font: { color: '#f1f5f9' } },
    margin: { l: 50, r: 50, t: 50, b: 50 },
    autosize: true,
    paper_bgcolor: 'rgba(0,0,0,0)',
    plot_bgcolor: 'rgba(0,0,0,0)',
  };

  const config = {
    displayModeBar: true,
    displaylogo: false,
    responsive: true,
    modeBarButtonsToRemove: ["pan2d", "lasso2d", "select2d"] as any,
  };

  return (
    <div className="vaccine-demo__chart-container">
      <div className="vaccine-demo__chart-header">
        <div className="vaccine-demo__chart-title">{title}</div>
      </div>
      <div className="vaccine-demo__chart">
        <Plot
          data={plotData}
          layout={layout}
          config={config}
          style={{ width: "100%", height: "400px" }}
          useResizeHandler
        />
      </div>
    </div>
  );
};

interface ComparisonChartProps {
  scenarios: Array<{
    name: string;
    Net: number;
    Savings: number;
    Costs: number;
    QALYs: number;
  }>;
  title?: string;
}

export const ComparisonChart: React.FC<ComparisonChartProps> = ({
  scenarios,
  title = "Scenario comparison",
}) => {
  if (scenarios.length === 0) {
    return (
      <div className="vaccine-demo__chart-container">
        <div className="vaccine-demo__chart-title">{title}</div>
        <div className="vaccine-demo__empty-state">
          No scenarios yet. Click <em>Save current</em> to add one.
        </div>
      </div>
    );
  }

  const plotData = [
    {
      x: scenarios.map((s) => s.name),
      y: scenarios.map((s) => s.Net),
      type: "bar" as const,
      name: "Net",
      marker: { color: "#10b981" },
    },
    {
      x: scenarios.map((s) => s.name),
      y: scenarios.map((s) => s.Savings),
      type: "bar" as const,
      name: "Savings",
      marker: { color: "#3b82f6" },
    },
    {
      x: scenarios.map((s) => s.name),
      y: scenarios.map((s) => s.Costs),
      type: "bar" as const,
      name: "Costs",
      marker: { color: "#ef4444" },
    },
  ];

  const layout = {
    title: { text: title, font: { size: 16, color: '#f1f5f9' } },
    xaxis: {
      title: { text: "Scenarios", font: { color: '#94a3b8' } },
      tickangle: -45,
      tickfont: { color: '#94a3b8' },
      gridcolor: '#334155',
      zerolinecolor: '#334155'
    },
    yaxis: { 
      title: { text: "EUR", font: { color: '#94a3b8' } },
      tickfont: { color: '#94a3b8' },
      gridcolor: '#334155',
      zerolinecolor: '#334155'
    },
    showlegend: true,
    legend: { font: { color: '#f1f5f9' } },
    margin: { l: 50, r: 20, t: 50, b: 80 },
    autosize: true,
    barmode: "group" as const,
    paper_bgcolor: 'rgba(0,0,0,0)',
    plot_bgcolor: 'rgba(0,0,0,0)',
  };

  const config = {
    displayModeBar: true,
    displaylogo: false,
    responsive: true,
    modeBarButtonsToRemove: ["pan2d", "lasso2d", "select2d"] as any,
  };

  return (
    <div>
      <div className="vaccine-demo__chart-header">
        <div className="vaccine-demo__chart-title">{title}</div>
      </div>
      <div className="vaccine-demo__chart">
        <Plot
          data={plotData}
          layout={layout}
          config={config}
          style={{ width: "100%", height: "400px" }}
          useResizeHandler
        />
      </div>
    </div>
  );
};
